import {abort, isRequestAction, isResponseAction, resetRequests} from '@redux-requests/core';
import {deleteToken, setToken} from './auth/auth';

export const purgeLegacyResponses = actionTypes => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (actionTypes.some(type => key.startsWith(type))) localStorage.removeItem(key);
    });
  } catch {
    // Disabled storage must not turn an API response into a failure.
  }
};

export const createSessionBoundary = actionTypes => {
  let generation = 0;
  const guard =
    callback =>
    (value, action, ...args) => {
      if (action.meta.sessionGeneration !== generation) {
        // Redux Requests recognizes this result without dispatching success handlers.
        throw Object.assign(new Error('Session changed'), {
          action: {type: abort(action.type)},
          isAborted: true,
        });
      }
      return callback(value, action, ...args);
    };
  return {
    guard,
    middleware:
      ({dispatch}) =>
      next =>
      action => {
        if ([deleteToken.toString(), setToken.toString()].includes(action.type)) {
          generation += 1;
          dispatch(resetRequests());
          purgeLegacyResponses(actionTypes);
          try {
            Object.keys(sessionStorage).forEach(key => {
              if (key.startsWith('cdo-order-attempt:')) sessionStorage.removeItem(key);
            });
          } catch {
            // Storage access can be disabled independently of authentication.
          }
        }
        if (isResponseAction(action) && action.meta.sessionGeneration !== generation) return action;
        if (isRequestAction(action)) {
          return next({
            ...action,
            meta: {
              ...action.meta,
              sessionGeneration: generation,
              ...(action.meta?.onSuccess ? {onSuccess: guard(action.meta.onSuccess)} : {}),
              ...(action.meta?.onError ? {onError: guard(action.meta.onError)} : {}),
            },
          });
        }
        return next(action);
      },
    wrapDriver:
      driver =>
      (request, action, ...args) => {
        const pending = driver(request, action, ...args);
        const result = Promise.resolve(pending).then(
          response => {
            // Run before global AND per-action success handlers (login, order, notifications).
            if (action.meta.sessionGeneration !== generation) throw 'REQUEST_ABORTED'; // eslint-disable-line no-throw-literal
            return response;
          },
          error => {
            if (action.meta.sessionGeneration !== generation) throw 'REQUEST_ABORTED'; // eslint-disable-line no-throw-literal
            throw error;
          },
        );
        if (pending.cancel) result.cancel = () => pending.cancel();
        return result;
      },
  };
};
